import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useTranslation } from 'react-i18next';
import { useSession } from 'src/contexts/SessionContext';
import { PlatformTab } from 'src/models/platform/_types';
import { FeatureFlag } from 'src/models/platform/_types';
import {
  getCombinedClientNavItems,
  isHQPlatform,
} from 'src/models/platform/utils';
import { isTrainer } from 'src/models/user/utils';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';

import { useHQPermissionCheck } from '../HQ/useHQPermissionCheck';
import { usePermissionCheck } from '../usePermissionCheck';

import {
  clientRoutesExtraData,
  defaultIcon,
  routesExtraData,
} from './routeConfig';

export interface DecoratedNavItem extends PlatformTab {
  reactRoute?: string;
  icon?: IconDefinition;
  children?: DecoratedNavItem[];
  target?: string;
  rel?: string;
}

export const useMainNav = (): DecoratedNavItem[] => {
  const { session, user } = useSession();
  const {
    canViewTrainerTab,
    canViewScheduleSubtab,
    canViewTrainerSidebarTabs,
    canViewClientTab,
  } = usePermissionCheck();

  const { canViewHQTab, canViewHQSidebarTab } = useHQPermissionCheck();

  const { t } = useTranslation('translation', { keyPrefix: 'menu' });
  const navType = isTrainer(user) ? 'trainerDashboard' : 'clientDashboard';
  const accountNavType = isTrainer(user) ? 'trainerAccount' : 'clientAccount';

  // Use different permission checks based on platform type
  const isHQ = isHQPlatform(session);
  const canViewTab = isHQ ? canViewHQTab : canViewTrainerTab;
  const canViewSidebarTab = isHQ
    ? canViewHQSidebarTab
    : canViewTrainerSidebarTabs;

  // Load main navigation tabs from API
  let navItems = session?.platformTabs[navType] || [];
  // Load "account" subtabs from API
  let accountItems = session?.platformTabs[accountNavType] || [];

  if (isTrainer(user)) {
    // Apply permissions to main navigation tabs for trainers
    navItems = navItems.filter((n) => {
      return canViewTab(n);
    });

    // Apply permissions to account subtabs for trainers
    accountItems = accountItems.filter((n) => {
      return canViewSidebarTab(n);
    });

    if (hasFeatureFlag(session, FeatureFlag.Onboarding)) {
      accountItems = [
        ...accountItems,
        {
          id: 'Onboarding',
          name: 'Onboarding',
          route: '/onboarding',
          enabled: true,
        },
      ];
    }

    if (hasFeatureFlag(session, FeatureFlag.NewDashboard) || user?.newDashboard) {
      accountItems = accountItems.filter((n) => {
        return n.id !== 'Business Dashboard';
      });
    }

    return [
      ...navItems.map((item) => {
        // Get the translation key
        const translationKey = `trainerDashboard.${item.id}`;

        // Check if user has overridden the default name
        // (if id and name don't match, it's overridden)
        const name =
          item.id === item.name
            ? t(translationKey, { defaultValue: item.name })
            : item.name;

        const extraItem = {
          ...item,
          ...routesExtraData[item.id],
          name,
          icon: routesExtraData[item.id]?.icon || defaultIcon,
        };

        // Only add reactRoute for Clients if feature flag is enabled
        if (item.id === 'Clients') {
          return {
            ...extraItem,
            reactRoute: hasFeatureFlag(session, FeatureFlag.NewClientsPage)
              ? routesExtraData.Clients.reactRoute
              : undefined,
          };
        }

        if (item.id === 'Dashboard') {
          return {
            ...extraItem,
            reactRoute: (hasFeatureFlag(session, FeatureFlag.NewDashboard) || user?.newDashboard)
              ? routesExtraData.Dashboard.reactRoute
              : undefined,
          };
        }

        if (item.id === 'Automations') {
          return {
            ...extraItem,
            reactRoute: hasFeatureFlag(session, FeatureFlag.NewAutomations)
              ? routesExtraData.Automations.reactRoute
              : undefined,
          };
        }

        // Use new React calendar route for Schedule parent if user has opted in
        if (item.id === 'Schedule' && user?.newCalendar) {
          return {
            ...extraItem,
            reactRoute: '/calendar',
            route: undefined,
            children: (extraItem.children || [])
              .filter((n) => canViewScheduleSubtab(n))
              .map((child) => {
                const childKey = `schedule.${child.id}`;
                const childName =
                  child.id === child.name
                    ? t(childKey, { defaultValue: child.name })
                    : child.name;

                if (child.id === 'calendar') {
                  return {
                    ...child,
                    name: childName,
                    reactRoute: '/calendar',
                    route: undefined,
                  };
                }

                return {
                  ...child,
                  name: childName,
                };
              }),
          };
        }

        return {
          ...extraItem,
          // Apply permissions to schedule subtabs
          children: (extraItem.children || [])
            .filter((n) => {
              if (extraItem.id === 'Schedule') return canViewScheduleSubtab(n);
              return true;
            })
            .map((child) => {
              // For each child, check if it's overridden
              const childKey = `schedule.${child.id}`;
              const childName =
                child.id === child.name
                  ? t(childKey, { defaultValue: child.name })
                  : child.name;

              // Use new React calendar route if user has opted in
              if (child.id === 'calendar' && user?.newCalendar) {
                return {
                  ...child,
                  name: childName,
                  reactRoute: '/calendar',
                  route: undefined,
                };
              }

              return {
                ...child,
                name: childName,
              };
            }),
        };
      }),
      {
        id: 'Account',
        name: t('trainerDashboard.Account', { defaultValue: 'Account' }),
        icon: routesExtraData.Account.icon,
        enabled: true,
        route: '/' + (accountItems[0]?.route?.replaceAll('.', '/') || ''),
        reactRoute: routesExtraData[accountItems[0]?.id]?.reactRoute,
        children: accountItems.map((childRoute) => {
          // For each account item, check if it's overridden
          const childKey = `trainerAccount.${childRoute.id}`;
          const childName =
            childRoute.id === childRoute.name
              ? t(childKey, { defaultValue: childRoute.name })
              : childRoute.name;

          const extraData = routesExtraData[childRoute.id] || {};
          // Recursively merge children from routesExtraData if present
          const mergedChildren = extraData.children
            ? extraData.children.map((grandchild) => ({
                ...grandchild,
                ...(routesExtraData[grandchild.id] || {}),
                ...(routesExtraData[grandchild.id]?.children
                  ? { children: routesExtraData[grandchild.id].children }
                  : {}),
              }))
            : undefined;

          return {
            ...childRoute,
            ...extraData,
            route: '/' + childRoute.route.replaceAll('.', '/'),
            name: childName,
            ...(mergedChildren ? { children: mergedChildren } : {}),
          };
        }),
      },
    ];
  } else {
    // Client navigation
    const combinedClientNavItems = getCombinedClientNavItems(
      session?.platformTabs
    ).filter((item) => {
      return canViewClientTab(item);
    });

    const clientExtraData = clientRoutesExtraData;

    return combinedClientNavItems.map((item) => {
      // Get the translation key
      const translationKey = `clientDashboard.${item.id}`;

      // Check if user has overridden the default name
      const name =
        item.id === item.name
          ? t(translationKey, { defaultValue: item.name })
          : item.name;

      const extraItem = {
        ...item,
        ...(clientExtraData[item.id] || routesExtraData[item.id]),
        target: clientExtraData[item.id]?.externalLink ? '_blank' : undefined,
        rel: clientExtraData[item.id]?.externalLink
          ? 'noopener noreferrer'
          : undefined,
        name,
        icon:
          clientExtraData[item.id]?.icon ||
          routesExtraData[item.id]?.icon ||
          defaultIcon,
      };

      // For clients, account items are nested under "My Stuff"
      if (item.id === 'My Stuff') {
        return {
          ...extraItem,
          route: '/' + (accountItems[0]?.route?.replaceAll('.', '/') || ''),
          reactRoute: routesExtraData[accountItems[0]?.id]?.reactRoute,
          children: accountItems.map((childRoute) => {
            // For each account item, check if it's overridden
            const childKey = `clientAccount.${childRoute.id}`;
            const childName =
              childRoute.id === childRoute.name
                ? t(childKey, { defaultValue: childRoute.name })
                : childRoute.name;

            // Use React route for connections if wearable integration feature flag is enabled
            if (childRoute.id === 'My Connected Apps') {
              return {
                ...childRoute,
                ...routesExtraData[childRoute.id],
                name: 'My Connected Apps',
                reactRoute: hasFeatureFlag(
                  session,
                  FeatureFlag.WearableIntegration
                )
                  ? '/connections'
                  : routesExtraData[childRoute.id]?.reactRoute,
                route: '/' + childRoute.route.replaceAll('.', '/'),
              };
            }

            return {
              ...childRoute,
              ...routesExtraData[childRoute.id],
              route: '/' + childRoute.route.replaceAll('.', '/'),
              name: childName,
            };
          }),
        };
      }

      return extraItem;
    });
  }
};
